import React, { useState, useEffect, useMemo, useCallback } from "react";
import ImageSelectDropMenu from "../../imageSelectDropMenu";
import CommonSelect from "../../../../core/common/commonSelect";
import { toProperCase } from "../../../../helper/company_helper";
import { TreeSelect } from "antd";

interface AssignAssetFilterProps {
  data: any[];
  setFilteredData: React.Dispatch<React.SetStateAction<any[]>>;
  assetCategoryOptions: any[];
  searchQuery?: any;
  setSearchQuery?: any;
}
interface CategoryNode {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: CategoryNode[];
}

const AssignAssetFilter: React.FC<AssignAssetFilterProps> = ({
  data,
  setFilteredData,
  assetCategoryOptions,
  searchQuery,
  setSearchQuery,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string | any>(undefined);
  const [assignedToFilter, setAssignedToFilter] = useState<string>("");
  const [assignedTypeFilter, setAssignedTypeFilter] = useState<string>("");
  const [selectedAssignee, setSelectedAssignee] = useState<any>(null);

  const assigneeOptions = useMemo(() => {
    const uniqueMap = new Map();

    data.forEach((item) => {
      if (
        item.assigned_to_type == "user" &&
        item.assigned_to &&
        !uniqueMap.has(item.assigned_to)
      ) {
        uniqueMap.set(item.assigned_to, {
          label: `${item.assigned_to_name} - [${
            item?.AssignedToDetails?.emp_id || "N/F"
          }]`,
          value: item.assigned_to,
          imageUrl: item.assigned_to_logo,
        });
      }
    });

    return Array.from(uniqueMap.values());
  }, [data]);

  const activeFiltersCount = useMemo(
    () =>
      [
        statusFilter,
        assignedToFilter,
        assignedTypeFilter,
        categoryFilter,
        searchQuery,
      ].filter((filter) => filter && filter !== "").length,
    [
      statusFilter,
      assignedToFilter,
      assignedTypeFilter,
      categoryFilter,
      searchQuery,
    ]
  );

  const handleAssigneeChange = (selected: any) => {
    const value = selected?.value || "";
    setAssignedToFilter(value);
    setSelectedAssignee(value ? selected : null);
  };

  const handleClearFilters = useCallback(() => {
    setStatusFilter("");
    setAssignedToFilter("");
    setAssignedTypeFilter("");
    setCategoryFilter(undefined);
    setSearchQuery("");
    setSelectedAssignee({ label: "All Assignees", value: "" });
  }, [setSearchQuery]);

  // Optimized filtering effect with debouncing for search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let newFilteredData = [...data];

      if (searchQuery?.trim()) {
        const query = searchQuery.toLowerCase().trim();
        newFilteredData = newFilteredData.filter(
          (item) =>
            item?.ProductDetails?.brand?.toLowerCase().includes(query) ||
            item?.ProductDetails?.purchase_from
              ?.toLowerCase()
              .includes(query) ||
            item?.asset_code?.toLowerCase().includes(query) ||
            item?.assigned_by_name?.toLowerCase().includes(query) ||
            item?.assigned_to_name?.toLowerCase().includes(query) ||
            item?.assigned_to_type?.toLowerCase().includes(query) ||
            item?.product_model?.toLowerCase().includes(query) ||
            item?.product_name?.toLowerCase().includes(query) ||
            item?.serial_no?.toLowerCase().includes(query)
        );
      }
      if (statusFilter && statusFilter !== "") {
        newFilteredData = newFilteredData.filter(
          (item) => item?.status === statusFilter
        );
      }
      if (categoryFilter && categoryFilter !== "") {
        newFilteredData = newFilteredData.filter((item) => {
          return item?.ProductDetails?.category?.path?.some(
            (pathItem: any) =>
              pathItem?.id?.toString() === categoryFilter?.toString()
          );
        });
      }
      if (assignedToFilter && assignedToFilter !== "") {
        newFilteredData = newFilteredData.filter(
          (item) => item?.assigned_to == assignedToFilter
        );
      }
      if (assignedTypeFilter && assignedTypeFilter !== "") {
        newFilteredData = newFilteredData.filter(
          (item) => item?.assigned_to_type === assignedTypeFilter
        );
      }
      setFilteredData(newFilteredData);
    }, 300); // 300ms debounce for search
    return () => clearTimeout(timeoutId);
  }, [
    statusFilter,
    assignedToFilter,
    assignedTypeFilter,
    categoryFilter,
    searchQuery,
    data,
    setFilteredData,
  ]);

  // Category Updated
  const handleCategorySelectionChange = (categoryValue: string) => {
    setCategoryFilter(categoryValue);
  };

  const categoryTreeData = useMemo(() => {
    const convertCategoriesToTreeFormat = (
      categories: CategoryNode[]
    ): any[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .filter((category) => category && category.status === "Active")
        .map((category) => {
          const hasChildCategories =
            category.subcategories && category.subcategories.length > 0;

          return {
            title: category.name,
            value: category.id,
            key: category.id,
            children: hasChildCategories
              ? convertCategoriesToTreeFormat(category.subcategories!)
              : undefined,
          };
        });
    };

    return convertCategoriesToTreeFormat(assetCategoryOptions || []);
  }, [assetCategoryOptions]);

  return (
    <div className="d-flex my-xl-auto align-items-center flex-wrap justify-content-end gap-2">
      {/* Category Filter */}
      <div
        className="ant-tree-select-div me-2"
        style={{ width: "250px" }}
        title="Category Filter">
        <TreeSelect
          value={categoryFilter}
          showSearch
          treeNodeFilterProp="title"
          filterTreeNode={(input, node) => {
            if (!input || !node) return false;
            const inputLower = input.toLowerCase();
            const title = node.title?.toString().toLowerCase() || "";
            return title.includes(inputLower);
          }}
          searchValue={undefined}
          autoClearSearchValue={true}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          treeData={categoryTreeData}
          placeholder="Please select a category"
          allowClear
          treeDefaultExpandAll={false}
          onChange={handleCategorySelectionChange}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement || document.body
          }
        />
      </div>

      {/* Status Filter */}
      <div
        className="me-2 fs-13"
        style={{ minWidth: "130px" }}
        title="Status Filter">
        <CommonSelect
          options={[
            { value: "", label: "Select Status" },
            { value: "Active", label: "Active" },
            { value: "Inactive", label: "Inactive" },
          ]}
          defaultValue={
            statusFilter
              ? { value: statusFilter, label: statusFilter }
              : { value: "", label: "Select Status" }
          }
          onChange={(selected: any) => setStatusFilter(selected?.value || "")}
          placeHolder="Select Status"
        />
      </div>

      {/* Assigned Type Filter */}
      <div
        className="me-2 fs-13"
        style={{ minWidth: "142px" }}
        title="Status Filter">
        <CommonSelect
          options={[
            { value: "", label: "All Assignment" },
            { value: "user", label: "User" },
            { value: "department", label: "Department" },
            { value: "location", label: "Location" },
            { value: "not_assigned", label: "Not Assigned" },
          ]}
          onChange={(selected: any) =>
            setAssignedTypeFilter(selected?.value || "")
          }
          defaultValue={
            assignedTypeFilter
              ? {
                  value: assignedTypeFilter,
                  label: toProperCase(assignedTypeFilter),
                }
              : { value: "", label: "All Assignment" }
          }
          placeHolder="Select Assignment Type"
        />
      </div>

      {/* Assigned To User Filter */}
      <div style={{ width: "200px" }} title="Filter By Assignee">
        <ImageSelectDropMenu
          className="truncate"
          options={[{ label: "All Assignees", value: "" }, ...assigneeOptions]}
          defaultValue={selectedAssignee}
          onChange={handleAssigneeChange}
          placeHolder="Select Assignee"
        />
      </div>
      {/* Clear Filters Button */}
      <div className="me-2">
        <button
          style={{ width: "120px" }}
          className="btn btn-outline-danger d-flex align-items-center position-relative justify-content-center"
          onClick={handleClearFilters}
          disabled={activeFiltersCount === 0}>
          <i className="ti ti-x me-2" />
          Clear
          {activeFiltersCount > 0 && (
            <span className="badge bg-danger rounded-pill ms-2">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default AssignAssetFilter;
